import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Collapse, Divider, Empty, Input, Select } from 'antd';
import { interpolateString, transformToTitleCase } from '@utils/string';
import CollapseSVG from '@assets/icons/collapse.svg?react';
import ExpandSVG from '@assets/icons/expand.svg?react';
import {
  Permission,
  PermissionAction,
  PermissionAsset,
  PermissionOrganization,
} from '@redux/types/types';
import {
  ACCESS_TEXT,
  CHOOSE_PERMISSIONS_TEXT,
  COLLAPSE_ALL_TEXT,
  EXPAND_ALL_TEXT,
  LEARN_MORE_TEXT,
  LEARN_MORE_URL,
  LEARN_MORE_WITH_BUTTON,
  NO_PERMISSIONS_FOUND_TEXT,
  PERMISSION_ACCESS_OPTIONS,
  PERMISSION_ACTIONS_LIST,
  PERMISSION_WILDCARD,
  SEARCH_PERMISSIONS_TEXT,
} from '@modules/Roles/constants/roles';
import styles from './PermissionsCollapseList.module.scss';

const { Search } = Input;

interface PermissionsListProps {
  permissions: PermissionOrganization[];
  onPermissionsChange?: (selectedPermissions: Permission[]) => void;
  initialSelectedPermissions?: Permission[];
}

const PERMISSION_WITHOUT_FAMILY = 'N/A';
const SEARCH_INPUT_TEST_ID = 'permission-search-input';

const PermissionsCollapseList = ({
  permissions,
  onPermissionsChange,
  initialSelectedPermissions,
}: PermissionsListProps) => {
  const [families, setFamilies] = useState<string[]>([]);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, string>>(() => {
    if (initialSelectedPermissions) {
      const newSelectedPermissions: Record<string, string> = {};

      initialSelectedPermissions.forEach((permission) => {
        if (permission.asset === PERMISSION_WILDCARD) {
          // Expand wildcard permission to all assets
          permissions.forEach((p) => {
            newSelectedPermissions[p.asset] = permission.action;
          });
        } else {
          newSelectedPermissions[permission.asset] = permission.action;
        }
      });

      return newSelectedPermissions;
    }
    return {};
  });
  const permissionAccessSelectRef = useRef<HTMLDivElement>(null);

  // Get all families from the permissions and create a list of unique families
  useEffect(() => {
    setFamilies([
      ...new Set(
        permissions
          .map((permission) => permission.family)
          .filter(
            (family): family is string =>
              family !== undefined && family !== PERMISSION_WITHOUT_FAMILY,
          ),
      ),
    ]);
  }, [permissions]);

  // Filter the permissions based on the search term
  const filteredPermissions = useMemo(() => {
    if (!permissionSearch.trim()) return permissions;

    return permissions.filter((permission) => {
      const assetName = permission.asset.toLowerCase();
      const assetTitleCase = transformToTitleCase(permission.asset).toLowerCase();
      const searchTerm = permissionSearch.toLowerCase();

      return assetName.includes(searchTerm) || assetTitleCase.includes(searchTerm);
    });
  }, [permissions, permissionSearch]);

  // Filter the families based on the search term
  const filteredFamilies = useMemo(() => {
    if (!permissionSearch.trim()) return families;

    const filteredFamilyPermissions = filteredPermissions.filter((permission) => permission.family);
    return [...new Set(filteredFamilyPermissions.map((permission) => permission.family!))];
  }, [families, filteredPermissions, permissionSearch]);

  const handleExpandAll = () => {
    setExpandedKeys(filteredFamilies);
  };

  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  useEffect(() => {
    setExpandedKeys(filteredFamilies);
  }, [filteredFamilies]);

  // Update selected permissions when initialSelectedPermissions changes
  useEffect(() => {
    if (initialSelectedPermissions) {
      const newSelectedPermissions: Record<string, string> = {};

      initialSelectedPermissions.forEach((permission) => {
        if (permission.asset === PERMISSION_WILDCARD) {
          // Expand wildcard permission to all assets
          permissions.forEach((p) => {
            newSelectedPermissions[p.asset] = permission.action;
          });
        } else {
          newSelectedPermissions[permission.asset] = permission.action;
        }
      });

      setSelectedPermissions(newSelectedPermissions);
    }
  }, [initialSelectedPermissions, permissions]);

  const handlePermissionChange = (asset: string, action: string) => {
    const newSelectedPermissions = { ...selectedPermissions };

    if (action === PERMISSION_ACCESS_OPTIONS.NO_ACCESS) {
      // Remove the specific permission
      delete newSelectedPermissions[asset];

      // If this was a wildcard permission, remove all permissions
      if (asset === PERMISSION_WILDCARD) {
        setSelectedPermissions({});
        onPermissionsChange?.([]);
        return;
      }
    } else {
      if (asset === PERMISSION_WILDCARD) {
        // Apply the action to all assets
        permissions.forEach((permission) => {
          newSelectedPermissions[permission.asset] = action;
        });
      } else {
        newSelectedPermissions[asset] = action;
      }
    }

    setSelectedPermissions(newSelectedPermissions);

    // Convert to the required format and notify parent
    const formattedPermissions = Object.entries(newSelectedPermissions).map(
      ([permissionAsset, permissionAction]) => ({
        action: permissionAction as PermissionAction,
        asset: permissionAsset as PermissionAsset,
      }),
    );
    onPermissionsChange?.(formattedPermissions);
  };

  const renderPermissionList = (permissionList: PermissionOrganization[]) => (
    <>
      {permissionList.map((permission, index) => (
        <div key={permission.asset}>
          <div className={styles.permissionItem}>
            <div className={styles.permissionContent}>
              <h6>{transformToTitleCase(permission.asset)}</h6>
              {permission.description && (
                <p className={styles.permissionDescription}>
                  {permission.description}
                  {interpolateString(LEARN_MORE_WITH_BUTTON, {
                    learnMoreButton: (
                      <Button
                        type="link"
                        size="small"
                        className={styles.permissionDescriptionButtonLink}
                        onClick={() => window.open(LEARN_MORE_URL, '_blank')}
                      >
                        {LEARN_MORE_TEXT}
                      </Button>
                    ),
                  })}
                </p>
              )}
            </div>
            <div className={styles.permissionAccess}>
              <label htmlFor={permission.asset}>{ACCESS_TEXT}</label>
              <div className={styles.permissionAccessSelect} ref={permissionAccessSelectRef}>
                <Select
                  id={permission.asset}
                  value={
                    selectedPermissions[permission.asset] || PERMISSION_ACCESS_OPTIONS.NO_ACCESS
                  }
                  popupMatchSelectWidth={false}
                  placement="bottomRight"
                  getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                  options={Object.values(PERMISSION_ACTIONS_LIST).map((option) => ({
                    label: option.label,
                    value: option.value,
                    description: option.description,
                  }))}
                  optionRender={(option) => {
                    return (
                      <div className={styles.permissionActionItem}>
                        <h6>{option.label}</h6>
                        {option.data?.description && (
                          <p className={styles.permissionActionDescription}>
                            {option.data.description}
                          </p>
                        )}
                      </div>
                    );
                  }}
                  onChange={(value) => handlePermissionChange(permission.asset, value)}
                />
              </div>
            </div>
          </div>
          {index < permissionList.length - 1 && <Divider className={styles.permissionDivider} />}
        </div>
      ))}
    </>
  );

  return (
    <div className={styles.permissionFamilies}>
      <h6>{CHOOSE_PERMISSIONS_TEXT}</h6>
      <div className={styles.permissionActions}>
        <Search
          placeholder={SEARCH_PERMISSIONS_TEXT}
          value={permissionSearch}
          onChange={(e) => setPermissionSearch(e.target.value)}
          onSearch={(value) => setPermissionSearch(value)}
          className={styles.permissionSearch}
          allowClear
          data-testid={SEARCH_INPUT_TEST_ID}
        />
        <div className={styles.permissionActionsButtons}>
          <Button
            type="text"
            onClick={handleExpandAll}
            icon={<ExpandSVG />}
            disabled={!!permissionSearch.trim()}
          >
            {EXPAND_ALL_TEXT}
          </Button>
          <Button
            type="text"
            onClick={handleCollapseAll}
            icon={<CollapseSVG />}
            disabled={!!permissionSearch.trim()}
          >
            {COLLAPSE_ALL_TEXT}
          </Button>
        </div>
      </div>
      {permissionSearch.trim() ? (
        <div className={styles.searchResults}>
          {filteredPermissions.length > 0 ? (
            renderPermissionList(filteredPermissions)
          ) : (
            <Empty description={NO_PERMISSIONS_FOUND_TEXT} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </div>
      ) : (
        <Collapse
          expandIconPosition="end"
          activeKey={expandedKeys}
          onChange={setExpandedKeys}
          items={filteredFamilies.map((family) => {
            const familyPermissions = filteredPermissions.filter(
              (permission) => permission.family === family,
            );
            return {
              key: family,
              label: family,
              children: <>{renderPermissionList(familyPermissions)}</>,
            };
          })}
        />
      )}
    </div>
  );
};

export default PermissionsCollapseList;
